import { toast } from '@heidi/design-system/toast';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { Dispatch } from '@/store';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { type FeedbackType } from '../components/customer-feedback';
import type { EnrichedReference } from '@heidi/evidence/contracts';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { track } from '@/lib/analytics/track';

interface UseReferenceFeedbackProps {
  reference: EnrichedReference;
  index: number;
  chatId?: string;
  sessionId?: string;
  messageId?: string;
}

interface UseReferenceFeedbackReturn {
  // State
  selectedFeedback: FeedbackType;
  isModalOpen: boolean;
  feedbackComment: string;
  pendingFeedback: FeedbackType;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;

  // Actions
  handleFeedbackClick: (feedback: FeedbackType) => void;
  handleSubmit: () => void;
  handleCancel: () => void;
  setFeedbackComment: (comment: string) => void;
}

/**
 * Hook for managing reference feedback state and analytics tracking.
 * Similar to useCustomerFeedback but tracks evidence_reference_feedback_submitted.
 */
export const useReferenceFeedback = ({
  reference,
  index,
  chatId,
  sessionId,
  messageId,
}: UseReferenceFeedbackProps): UseReferenceFeedbackReturn => {
  const { t } = useTranslations();
  const dispatch = useDispatch<Dispatch>();
  const { baseProps } = useEvidenceAnalytics();

  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [pendingFeedback, setPendingFeedback] = useState<FeedbackType>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingFeedback(null);
    setFeedbackComment('');
  }, []);

  const handleFeedbackClick = useCallback(
    (feedback: FeedbackType) => {
      if (selectedFeedback === feedback) {
        setSelectedFeedback(null);
        if (isModalOpen) closeModal();
        return;
      }

      setSelectedFeedback(feedback);
      setPendingFeedback(feedback);
      setFeedbackComment('');
      setIsModalOpen(true);
    },
    [selectedFeedback, isModalOpen, closeModal]
  );

  const handleSubmit = useCallback(() => {
    if (!pendingFeedback) return;

    setIsSubmitting(true);

    try {
      track({ options: { amplitude: false },
        event_name: TrackingEventName.EvidenceReferenceFeedbackSubmitted,
        props: {
          ...baseProps,
          feedback_type: pendingFeedback,
          source_url: reference.url || '',
          source_domain: reference.domain,
          source_title: reference.title || undefined,
          reference_index: index,
          chat_id: chatId,
          session_id: sessionId,
          message_id: messageId,
          has_comment: !!feedbackComment.trim(),
          user_comment: feedbackComment.trim() || undefined,
        },
      });

      toast({
        tone: 'success',
        title: t('feedback.askAiFeedback.feedbackSubmitted'),
      });

      closeModal();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    pendingFeedback,
    feedbackComment,
    dispatch,
    baseProps,
    reference,
    index,
    chatId,
    sessionId,
    messageId,
    closeModal,
    t,
  ]);

  const handleCancel = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const isSubmitDisabled = false;

  return {
    // State
    selectedFeedback,
    isModalOpen,
    feedbackComment,
    pendingFeedback,
    isSubmitDisabled,
    isSubmitting,

    // Actions
    handleFeedbackClick,
    handleSubmit,
    handleCancel,
    setFeedbackComment,
  };
};
